import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';
import { SUPPORTED_LANGUAGES, type Language } from '../lib/i18n';
import { getLocaleLanguage, isPublicLocale, normalizePublicLocale, type PublicLocale } from '../lib/locales';

const STORAGE_KEY = 'admin_lang';
const LEGACY_STORAGE_KEY = 'tg_language';
const PUBLIC_LOCALE_STORAGE_KEY = 'menu_locale';
const supportedCodes = SUPPORTED_LANGUAGES.map(language => language.code);
const listeners = new Set<() => void>();

const isLanguage = (value: string | null): value is Language =>
  Boolean(value && supportedCodes.includes(value as Language));

const getBrowserLanguage = (): Language => {
  if (typeof navigator === 'undefined') return 'en';
  const browserLang = navigator.language.split('-')[0];
  return isLanguage(browserLang) ? browserLang : 'en';
};

const getStoredLanguage = (): Language => {
  if (typeof window === 'undefined') return 'en';

  // Strictly isolated Venue Admin language storage - top priority for administration
  const saved = window.localStorage.getItem(STORAGE_KEY) || window.localStorage.getItem(LEGACY_STORAGE_KEY);
  if (isLanguage(saved)) return saved;

  const isPublicMenuRoute = !window.location.pathname.startsWith('/admin') && !window.location.pathname.startsWith('/chefs') && !window.location.pathname.startsWith('/waiters');
  if (isPublicMenuRoute) {
    const savedLocale = window.localStorage.getItem(PUBLIC_LOCALE_STORAGE_KEY);
    if (isPublicLocale(savedLocale)) {
      return getLocaleLanguage(savedLocale);
    }
  }

  return getBrowserLanguage();
};

const getStoredLocale = (): PublicLocale | null => {
  if (typeof window === 'undefined') return null;
  const isPublicMenuRoute = !window.location.pathname.startsWith('/admin') && !window.location.pathname.startsWith('/chefs') && !window.location.pathname.startsWith('/waiters');
  if (!isPublicMenuRoute) return null;
  const savedLocale = window.localStorage.getItem(PUBLIC_LOCALE_STORAGE_KEY);
  return isPublicLocale(savedLocale) ? normalizePublicLocale(savedLocale) : null;
};

const applyDocumentLanguage = (language: Language, locale?: PublicLocale | null) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = locale || language;
};

const emitLanguageChange = () => {
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getServerSnapshot = (): Language => 'en';
const getServerLocaleSnapshot = (): PublicLocale | null => null;

export const useLanguage = () => {
  const currentLanguage = useSyncExternalStore(subscribe, getStoredLanguage, getServerSnapshot);
  const currentLocale = useSyncExternalStore(subscribe, getStoredLocale, getServerLocaleSnapshot);

  // Synchronize preferred language from authenticated user metadata on load
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      const userLang = user?.user_metadata?.preferred_language;
      if (isLanguage(userLang) && userLang !== getStoredLanguage()) {
        window.localStorage.setItem(STORAGE_KEY, userLang);
        window.localStorage.setItem(LEGACY_STORAGE_KEY, userLang);
        applyDocumentLanguage(userLang);
        emitLanguageChange();
      }
    }).catch(() => {});
  }, []);

  const setLanguage = useCallback((language: Language) => {
    window.localStorage.setItem(STORAGE_KEY, language);
    window.localStorage.setItem(LEGACY_STORAGE_KEY, language);
    applyDocumentLanguage(language);
    emitLanguageChange();

    // Persist operator's dashboard language across all sessions and devices
    supabase.auth.updateUser({
      data: { preferred_language: language }
    }).catch(err => {
      console.warn('Could not sync preferred_language to user metadata:', err);
    });
  }, []);

  const setPublicLocale = useCallback((locale: PublicLocale) => {
    const language = getLocaleLanguage(locale);
    window.localStorage.setItem(PUBLIC_LOCALE_STORAGE_KEY, locale);
    applyDocumentLanguage(language, locale);
    emitLanguageChange();
  }, []);

  useEffect(() => {
    applyDocumentLanguage(currentLanguage, currentLocale);
  }, [currentLanguage, currentLocale]);

  return { currentLanguage, currentLocale, setLanguage, setPublicLocale, languages: SUPPORTED_LANGUAGES };
};
